import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, closestCorners, pointerWithin, useDraggable, useDroppable, useSensor, useSensors,
  type CollisionDetection, type DragEndEvent, type DragStartEvent,
} from "@dnd-kit/core";
import { ArrowRight, MoreVertical } from "lucide-react";
import AppSelect from "@/components/ui/app-select";

/** What is under the pointer wins (bottom drop chips before the columns behind them); else the nearest column. */
const collision: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  if (hits.length) return [...hits].sort((a, b) => Number(String(b.id).startsWith("chip:")) - Number(String(a.id).startsWith("chip:")));
  return closestCorners(args);
};

export interface KanbanColumn<T> {
  id: string;
  title: string;
  /** Tailwind background class for the dot in the column header. */
  dot: string;
  items: T[];
  /** Total in this column when `items` is only the first page of it. */
  count?: number;
  /** Small line under the title, e.g. the column's total value. */
  subtitle?: ReactNode;
  /** False for columns a card can't be moved into (e.g. "Converted", reached by its own action). */
  droppable?: boolean;
}

interface Props<T> {
  columns: KanbanColumn<T>[];
  getId: (item: T) => string;
  renderCard: (item: T) => ReactNode;
  /**
   * Called after a card is dropped on another column. The card has already moved on screen;
   * return false (or throw) to put it back, e.g. when the move needs a confirmation first.
   */
  onMove: (item: T, from: string, to: string) => Promise<boolean | void> | boolean | void;
  onOpen?: (item: T) => void;
  canMove?: (item: T) => boolean;
  loading?: boolean;
  /** "lead" → "3 leads" in column badges. */
  noun?: string;
  emptyText?: string;
}

/**
 * Drag-and-drop board used by Leads, Deals, Projects and Tasks.
 * Mouse: drag after moving 6px (so a click still opens the card). Touch: press and hold for
 * a quarter second, so swiping still scrolls the board. Keyboard: focus a card, Space to lift,
 * arrows to move, Space to drop. On small screens a strip of drop targets (one per column) appears
 * at the bottom while dragging, so a card reaches any column without scrolling. Every card also
 * has a "Move to" menu, which works anywhere.
 *
 * Phones default to a card view instead of the board: status tabs with counts, one column's cards
 * at a time, and plain buttons on each card ("Next: …" and a "Move to…" picker). A Cards / Board
 * switch (remembered on the device) brings the swipeable board back.
 */
export default function Kanban<T>({ columns, getId, renderCard, onMove, onOpen, canMove = () => true, loading, noun = "item", emptyText = "Nothing here" }: Props<T>) {
  const [cols, setCols] = useState(columns);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  useEffect(() => { setCols(columns); }, [columns]);
  const small = useIsSmallScreen();
  const [phoneMode, setPhoneMode] = useState<"cards" | "board">(() => {
    try { return localStorage.getItem(PHONE_MODE_KEY) === "board" ? "board" : "cards"; } catch { return "cards"; }
  });
  const choosePhoneMode = (m: "cards" | "board") => {
    setPhoneMode(m);
    try { localStorage.setItem(PHONE_MODE_KEY, m); } catch { /* per-device convenience only */ }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const locate = useMemo(() => {
    const map = new Map<string, { item: T; column: string }>();
    cols.forEach((c) => c.items.forEach((item) => map.set(getId(item), { item, column: c.id })));
    return map;
  }, [cols, getId]);

  const move = async (id: string, to: string) => {
    const found = locate.get(id);
    if (!found || found.column === to) return;
    const target = cols.find((c) => c.id === to);
    if (!target || target.droppable === false) return;
    const before = cols;
    // Move on screen first so the board feels instant; undo if the page refuses or the save fails.
    setCols((current) => current.map((c) => {
      if (c.id === found.column) return { ...c, items: c.items.filter((i) => getId(i) !== id), count: c.count !== undefined ? c.count - 1 : undefined };
      if (c.id === to) return { ...c, items: [found.item, ...c.items], count: c.count !== undefined ? c.count + 1 : undefined };
      return c;
    }));
    try {
      if ((await onMove(found.item, found.column, to)) === false) setCols(before);
    } catch {
      setCols(before);
    }
  };

  const onDragStart = (e: DragStartEvent) => { setActiveId(String(e.active.id)); setMenuFor(null); };
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    if (e.over) move(String(e.active.id), String(e.over.id).replace(/^chip:/, ""));
  };
  const active = activeId ? locate.get(activeId)?.item : undefined;

  if (loading) {
    return (
      <div className="flex gap-4 overflow-hidden">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-80 w-[85vw] max-w-[18rem] shrink-0 animate-pulse rounded-xl bg-white/70 sm:w-72" />)}
      </div>
    );
  }

  const phoneSwitch = small && (
    <div className="mb-3 flex items-center justify-between gap-2 [-webkit-tap-highlight-color:transparent]">
      <span className="text-xs text-gray-500">{phoneMode === "cards" ? "Tap a status to see its cards" : "Hold a card, then drag"}</span>
      <div className="inline-flex rounded-full bg-gray-100 p-1 text-xs">
        {(["cards", "board"] as const).map((m) => (
          <button key={m} type="button" onClick={() => choosePhoneMode(m)} className={`rounded-full px-3 py-1 transition ${phoneMode === m ? "bg-white font-medium text-gray-900 shadow-sm" : "text-gray-600"}`}>
            {m === "cards" ? "Cards" : "Board"}
          </button>
        ))}
      </div>
    </div>
  );

  if (small && phoneMode === "cards") {
    return (
      <>
        {phoneSwitch}
        <PhoneCards cols={cols} getId={getId} renderCard={renderCard} canMove={canMove} onOpen={onOpen} move={move} noun={noun} emptyText={emptyText} />
      </>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
      {phoneSwitch}
      {/* Snap scrolling is paused while dragging so edge auto-scroll can move the board freely. */}
      <div className={`-mx-4 overflow-x-auto overscroll-x-contain px-4 pb-4 sm:mx-0 sm:px-0 ${activeId ? "" : "snap-x snap-mandatory sm:snap-none"}`}>
        <div className="flex min-w-max gap-4">
          {cols.map((col) => (
            <Column key={col.id} column={col} noun={noun} emptyText={emptyText} dragging={!!activeId}>
              {col.items.map((item) => {
                const id = getId(item);
                const movable = canMove(item);
                return (
                  <Card key={id} id={id} disabled={!movable} hidden={activeId === id} onOpen={onOpen ? () => onOpen(item) : undefined}
                    menu={movable ? (
                      <MoveMenu
                        open={menuFor === id}
                        onToggle={() => setMenuFor((m) => (m === id ? null : id))}
                        targets={cols.filter((c) => c.id !== col.id && c.droppable !== false)}
                        onPick={(to) => { setMenuFor(null); move(id, to); }}
                      />
                    ) : null}
                  >
                    {renderCard(item)}
                  </Card>
                );
              })}
            </Column>
          ))}
        </div>
      </div>
      {activeId && (
        <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-gray-200 bg-white/95 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur sm:hidden">
          <p className="mb-2 text-center text-[11px] font-medium uppercase tracking-wide text-gray-400">Drop on a column</p>
          <div className="flex gap-2 overflow-x-auto">
            {cols.filter((c) => c.droppable !== false).map((c) => (
              <DropChip key={c.id} column={c} current={locate.get(activeId)?.column === c.id} />
            ))}
          </div>
        </div>
      )}
      <DragOverlay dropAnimation={null}>
        {active ? <div className="w-[80vw] max-w-[17rem] rotate-2 cursor-grabbing rounded-xl border border-primary/40 bg-white p-4 shadow-xl sm:w-[17rem]">{renderCard(active)}</div> : null}
      </DragOverlay>
    </DndContext>
  );
}

const PHONE_MODE_KEY = "kanban_phone_mode";

/** True below Tailwind's `sm` breakpoint (phones), kept in sync with rotation and resizing. */
function useIsSmallScreen() {
  const query = "(max-width: 639px)";
  const [small, setSmall] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const update = () => setSmall(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return small;
}

/** Phone card view: status tabs, the selected column's cards, and move buttons on each card. */
function PhoneCards<T>({ cols, getId, renderCard, canMove, onOpen, move, noun, emptyText }: {
  cols: KanbanColumn<T>[];
  getId: (item: T) => string;
  renderCard: (item: T) => ReactNode;
  canMove: (item: T) => boolean;
  onOpen?: (item: T) => void;
  move: (id: string, to: string) => Promise<void>;
  noun: string;
  emptyText: string;
}) {
  const [tab, setTab] = useState(() => (cols.find((c) => c.items.length) ?? cols[0])?.id ?? "");
  const current = cols.find((c) => c.id === tab) ?? cols[0];
  if (!current) return null;
  const targets = cols.filter((c) => c.id !== current.id && c.droppable !== false);
  const order = cols.filter((c) => c.droppable !== false);
  const next = order[order.findIndex((c) => c.id === current.id) + 1];
  const total = current.count ?? current.items.length;

  return (
    <div className="[-webkit-tap-highlight-color:transparent]">
      <div className="-mx-4 mb-3 flex gap-2 overflow-x-auto px-4 pb-1" role="tablist">
        {cols.map((c) => (
          <button key={c.id} type="button" role="tab" aria-selected={c.id === current.id} onClick={() => setTab(c.id)}
            className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 text-xs transition [-webkit-tap-highlight-color:transparent] ${c.id === current.id ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-white text-gray-700"}`}>
            <span className={`h-2 w-2 rounded-full ${c.dot}`} />{c.title}
            <span className={`rounded-full px-1.5 text-[11px] ${c.id === current.id ? "bg-white/20" : "bg-gray-100 text-gray-600"}`}>{c.count ?? c.items.length}</span>
          </button>
        ))}
      </div>
      {current.subtitle && <p className="mb-2 text-xs text-gray-500">{current.subtitle}</p>}
      <div className="space-y-3">
        {current.items.map((item) => {
          const id = getId(item);
          const movable = canMove(item) && targets.length > 0;
          return (
            <article key={id} className="rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="p-4" onClick={onOpen ? () => onOpen(item) : undefined} role={onOpen ? "button" : undefined}>{renderCard(item)}</div>
              {movable && (
                <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2.5">
                  {next && next.id !== current.id && (
                    <button type="button" onClick={() => move(id, next.id)} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-white active:bg-primary/90">
                      Next: {next.title}<ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <label className="relative flex-1">
                    <span className="sr-only">Move to</span>
                    <AppSelect value="" onChange={(e) => { if (e.target.value) move(id, e.target.value); }}
                      className="w-full appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-center text-xs font-medium text-gray-700">
                      <option value="">Move to…</option>
                      {targets.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                    </AppSelect>
                  </label>
                </div>
              )}
            </article>
          );
        })}
        {current.items.length === 0 && <p className="rounded-xl border border-dashed border-gray-300 py-10 text-center text-xs text-gray-400">{emptyText}</p>}
        {total > current.items.length && <p className="text-center text-[11px] text-gray-400">Showing {current.items.length} of {total} {noun.toLowerCase()}s</p>}
      </div>
    </div>
  );
}

function DropChip<T>({ column, current }: { column: KanbanColumn<T>; current: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: `chip:${column.id}`, disabled: current });
  return (
    <div ref={setNodeRef} className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 text-xs transition ${isOver ? "border-primary bg-primary text-white" : current ? "border-gray-200 text-gray-300" : "border-gray-300 bg-white text-gray-700"}`}>
      <span className={`h-2 w-2 rounded-full ${isOver ? "bg-white" : column.dot}`} />{column.title}
    </div>
  );
}

function Column<T>({ column, noun, emptyText, dragging, children }: { column: KanbanColumn<T>; noun: string; emptyText: string; dragging: boolean; children: ReactNode }) {
  const accepts = column.droppable !== false;
  const { setNodeRef, isOver } = useDroppable({ id: column.id, disabled: !accepts });
  const total = column.count ?? column.items.length;
  return (
    <section ref={setNodeRef} className={`flex w-[85vw] max-w-[20rem] shrink-0 snap-start flex-col rounded-xl transition-colors sm:w-72 ${isOver ? "bg-primary/5 ring-2 ring-primary/40" : dragging && !accepts ? "opacity-60" : ""}`}>
      <header className="mb-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${column.dot}`} />
            <h3 className="truncate text-sm font-medium text-gray-800">{column.title}</h3>
          </div>
          <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">{total} {noun}{total === 1 ? "" : "s"}</span>
        </div>
        {column.subtitle && <p className="mt-1 truncate text-xs text-gray-500">{column.subtitle}</p>}
      </header>
      <div className="flex max-h-[calc(100vh-250px)] min-h-[8rem] flex-col gap-3 overflow-y-auto pb-1">
        {children}
        {column.items.length === 0 && (
          <p className="rounded-xl border border-dashed border-gray-300 py-8 text-center text-xs text-gray-400">{dragging && accepts ? "Drop here" : emptyText}</p>
        )}
        {column.count !== undefined && column.count > column.items.length && (
          <p className="py-1 text-center text-[11px] text-gray-400">Showing {column.items.length} of {column.count}</p>
        )}
      </div>
    </section>
  );
}

function Card({ id, disabled, hidden, onOpen, menu, children }: { id: string; disabled: boolean; hidden: boolean; onOpen?: () => void; menu: ReactNode; children: ReactNode }) {
  const { attributes, listeners, setNodeRef } = useDraggable({ id, disabled });
  return (
    <article
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onOpen}
      style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
      className={`relative select-none rounded-xl border border-gray-200 bg-white p-4 shadow-sm outline-none transition hover:border-primary/40 hover:shadow focus-visible:ring-2 focus-visible:ring-primary ${disabled ? "" : "cursor-grab active:cursor-grabbing"} ${hidden ? "opacity-30" : ""}`}
    >
      {menu}
      {children}
    </article>
  );
}

function MoveMenu<T>({ open, onToggle, targets, onPick }: { open: boolean; onToggle: () => void; targets: KanbanColumn<T>[]; onPick: (to: string) => void }) {
  // Pointer/key events stop here so pressing the menu never starts a drag or opens the card.
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();
  return (
    <div className="absolute right-2 top-2 z-10" onPointerDown={stop} onKeyDown={stop} onClick={stop}>
      <button type="button" aria-label="Move to another column" aria-expanded={open} onClick={onToggle} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-8 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          <p className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">Move to</p>
          {targets.map((t) => (
            <button key={t.id} type="button" onClick={() => onPick(t.id)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50">
              <span className={`h-2 w-2 rounded-full ${t.dot}`} />{t.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
